import { Grid, useTheme } from '@mui/material'
import { AlarmIcon, Approve, CloseIcon, LocationIcon } from 'assets/icons'
import IconButton from 'components/buttons/IconButton'
import ChangeTextDialog from 'components/dialogs/ChangeTextDialog'
import InputWithApproveButton from 'components/inputs/InputWithApproveButton'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import { TextWithLinks } from 'components/TextWithLinks'
import { IEventDetails } from 'hooks/useEventDetails'
import i18n from 'i18n'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EventLocation } from 'types/api/changes'
import { calculateZoomLevel, radiusInMeters } from 'utils/location_reminder'

interface EventLocationRowProps {
  disallowEdit?: boolean
  eventHook?: IEventDetails
  setLocation: (loc: EventLocation | undefined) => void
  eventLocation: EventLocation | undefined
  isUiBlocked?: boolean
}

export function EventLocationRow(props: EventLocationRowProps) {
  const theme = useTheme()
  const hasLocation =
    typeof props.eventLocation !== 'string' &&
    props.eventLocation != undefined &&
    Object.keys(props.eventLocation).length > 0

  const removeLocation = () => {
    props.setLocation(undefined)
  }

  const { t } = useTranslation()

  const onEventLocationChange = (event: ChangeEvent<HTMLInputElement>) => {
    props.eventHook?.setLocationPendingText(event.target.value)
  }

  const locationReminder = props.eventHook?.locationReminder?.at(0)

  const [openChange, setOpenChange] = useState(false)

  return hasLocation ? (
    <div>
      <PopupSectionRow
        icon={<LocationIcon />}
        cursor={props.eventLocation?.Address ? 'default' : 'pointer'}
        valuePlaceholder={!props.eventLocation?.Address}
        clickableTitle={!props.disallowEdit}
        title={
          <p
            id="event-location-title"
            color={'var(--color-negative)'}
            style={{
              paddingLeft: i18n.dir() === 'ltr' ? '6px' : undefined,
              paddingRight: i18n.dir() === 'rtl' ? '6px' : undefined
            }}
          >
            {props.eventLocation?.Address ? (
              <TextWithLinks text={props.eventLocation?.Address} />
            ) : (
              t('addLocation')
            )}
          </p>
        }
        action={() => {
          if (!props.isUiBlocked) setOpenChange(true)
        }}
        value={
          props.disallowEdit ? (
            <></>
          ) : (
            <IconButton
              stopPropagationOnClick
              size="xs"
              onClick={removeLocation}
            >
              <CloseIcon />
            </IconButton>
          )
        }
      />
      {locationReminder ? (
        <PopupSectionRow
          icon={<AlarmIcon />}
          cursor="default"
          onlyTitle
          overflow="hidden"
          title={t(
            locationReminder.approachEnabled
              ? 'remindWhenApproaching'
              : 'remindWhenDepartingBy',
            {
              radius: locationReminder.radius,
              unit:
                locationReminder.lenType === '1'
                  ? t('meters')
                  : locationReminder.lenType === '2'
                    ? t('yards')
                    : locationReminder.lenType === '3'
                      ? t('miles')
                      : t('kilometers')
            }
          )}
        />
      ) : (
        <></>
      )}
      {props.eventLocation?.Lat && props.eventLocation.Long && (
        <SimpleMap
          height={'150px'}
          lat={parseFloat(props.eventLocation?.Lat ?? '0.0')}
          lng={parseFloat(props.eventLocation?.Long ?? '0.0')}
          radius={
            props.eventHook?.locationReminder &&
            props.eventHook.locationReminder.length > 0
              ? radiusInMeters(
                  props.eventHook.locationReminder[0].lenType,
                  props.eventHook.locationReminder[0].radius
                )
              : 0
          }
        />
      )}
      <ChangeTextDialog
        open={openChange}
        setOpen={setOpenChange}
        title={t('addLocation')}
        text={props.eventLocation?.Address ?? ''}
        onApprove={(s: string) => {
          props.setLocation(undefined)
          props.eventHook?.addLocationToEvent(s)
        }}
        placeholder={t('addLocation')}
      />
    </div>
  ) : (
    <InputWithApproveButton
      onTextChange={onEventLocationChange}
      inputStyle={{}}
      stopPropagationOnClick
      leftIcon={<LocationIcon color={theme.palette.input.placeholder} />}
      hintText={`${t('addLocation')}`}
      onApprove={props.eventHook?.addLocationToEvent ?? (() => {})}
      approveIcon={
        <Approve
          style={{
            marginLeft: 8,
            marginRight: 8,
            color:
              theme.palette.mode === 'dark'
                ? 'white'
                : (theme.palette.input.placeholder as string),
            fill:
              theme.palette.mode === 'dark'
                ? 'white'
                : (theme.palette.input.placeholder as string)
          }}
        />
      }
      id="input-add-location"
    />
  )
}

interface SimpleMapProps {
  height: string
  lat: number
  lng: number
  radius?: number
}

export function SimpleMap(props: SimpleMapProps) {
  const theme = useTheme()
  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<any>(null)
  const circleRef = useRef<any>(null)

  const center = {
    lat: props.lat,
    lng: props.lng
  }

  const zoom = calculateZoomLevel(props.radius ?? 0)

  const mapStyles =
    theme.palette.mode === 'dark'
      ? [
          { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
          { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
          {
            elementType: 'labels.text.stroke',
            stylers: [{ color: '#242f3e' }]
          },
          {
            featureType: 'administrative.locality',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#d59563' }]
          },
          {
            featureType: 'poi',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#d59563' }]
          },
          {
            featureType: 'poi.park',
            elementType: 'geometry',
            stylers: [{ color: '#263c3f' }]
          },
          {
            featureType: 'poi.park',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#6b9a76' }]
          },
          {
            featureType: 'road',
            elementType: 'geometry',
            stylers: [{ color: '#38414e' }]
          },
          {
            featureType: 'road',
            elementType: 'geometry.stroke',
            stylers: [{ color: '#212a37' }]
          },
          {
            featureType: 'road',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#9ca5b3' }]
          },
          {
            featureType: 'road.highway',
            elementType: 'geometry',
            stylers: [{ color: '#746855' }]
          },
          {
            featureType: 'road.highway',
            elementType: 'geometry.stroke',
            stylers: [{ color: '#1f2835' }]
          },
          {
            featureType: 'road.highway',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#f3d19c' }]
          },
          {
            featureType: 'transit',
            elementType: 'geometry',
            stylers: [{ color: '#2f3948' }]
          },
          {
            featureType: 'transit.station',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#d59563' }]
          },
          {
            featureType: 'water',
            elementType: 'geometry',
            stylers: [{ color: '#17263c' }]
          },
          {
            featureType: 'water',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#515c6d' }]
          },
          {
            featureType: 'water',
            elementType: 'labels.text.stroke',
            stylers: [{ color: '#17263c' }]
          }
        ]
      : undefined

  useEffect(() => {
    let intervalId: number | undefined

    const initializeMap = () => {
      if (!mapContainerRef.current || !window.google?.maps?.Map) return false

      if (!mapRef.current) {
        mapRef.current = new window.google.maps.Map(mapContainerRef.current, {
          center,
          zoom,
          fullscreenControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          zoomControl: false,
          styles: mapStyles
        })
      }

      mapRef.current.setOptions({ styles: mapStyles })
      mapRef.current.setCenter(center)
      mapRef.current.setZoom(zoom)

      if ((props.radius ?? 0) > 0) {
        if (!circleRef.current) {
          circleRef.current = new window.google.maps.Circle({
            strokeColor: '#0084d3',
            strokeWeight: 3,
            fillColor: '#500084d3',
            map: mapRef.current
          })
        }

        circleRef.current.setOptions({
          map: mapRef.current,
          center,
          radius: props.radius
        })
      } else if (circleRef.current) {
        circleRef.current.setMap(null)
        circleRef.current = null
      }

      return true
    }

    if (!initializeMap()) {
      intervalId = window.setInterval(() => {
        if (initializeMap() && intervalId) {
          window.clearInterval(intervalId)
        }
      }, 250)
    }

    return () => {
      if (intervalId) {
        window.clearInterval(intervalId)
      }
    }
  }, [center, mapStyles, props.radius, zoom])

  useEffect(() => {
    return () => {
      if (circleRef.current) {
        circleRef.current.setMap(null)
      }

      if (mapRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(mapRef.current)
      }
    }
  }, [])

  if (props.lat === 0 && props.lng === 0) return <></>

  return (
    <Grid
      container
      style={{
        height: props.height,
        width: '100%'
      }}
    >
      <div
        ref={mapContainerRef}
        style={{
          height: '100%',
          width: '100%'
        }}
      />
    </Grid>
  )
}
